import { analyticsConfig, EVENTS, sanitizeEventProps } from "./config"
import {
  ensureDataLayer,
  getConsentPreferences,
  readStoredConsent,
  setDefaultGoogleConsent,
  updateGoogleConsent,
} from "./consent"

const GTM_SCRIPT_ID = "almagesto-gtm-script"
const GA4_SCRIPT_ID = "almagesto-ga4-script"

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined"

export const hasMarketingConsent = () =>
  readStoredConsent()?.preferences?.marketing === true

const appendScriptOnce = ({ id, src }) => {
  if (!isBrowser() || document.getElementById(id)) {
    return false
  }

  const script = document.createElement("script")
  script.id = id
  script.async = true
  script.src = src
  document.head.appendChild(script)

  return true
}

const loadGoogleTagManager = gtmId => {
  const gtag = ensureDataLayer()

  if (!gtag || document.getElementById(GTM_SCRIPT_ID)) {
    return false
  }

  window.dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  })

  return appendScriptOnce({
    id: GTM_SCRIPT_ID,
    src: `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
      gtmId
    )}`,
  })
}

const loadGoogleAnalytics = measurementId => {
  const gtag = ensureDataLayer()

  if (!gtag) {
    return false
  }

  const loaded = appendScriptOnce({
    id: GA4_SCRIPT_ID,
    src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      measurementId
    )}`,
  })

  if (!window.__almagestoGa4Configured) {
    window.__almagestoGa4Configured = true
    gtag("js", new Date())
    gtag("config", measurementId, {
      allow_google_signals: false,
      anonymize_ip: true,
      send_page_view: false,
    })
  }

  return loaded
}

export const shouldLoadMarketingTags = (
  preferences = getConsentPreferences(),
  config = analyticsConfig
) => config.marketing.canLoadTags && preferences.marketing === true

export const initializeMarketing = (config = analyticsConfig) => {
  if (!isBrowser() || !config.marketing.requested) {
    return false
  }

  setDefaultGoogleConsent()

  const preferences = getConsentPreferences()
  updateGoogleConsent(preferences)

  if (!shouldLoadMarketingTags(preferences, config)) {
    return false
  }

  if (config.marketing.gtmId) {
    return loadGoogleTagManager(config.marketing.gtmId)
  }

  if (config.marketing.ga4MeasurementId) {
    return loadGoogleAnalytics(config.marketing.ga4MeasurementId)
  }

  return false
}

export const applyMarketingConsent = (
  preferences,
  config = analyticsConfig
) => {
  if (!isBrowser() || !config.marketing.requested) {
    return false
  }

  setDefaultGoogleConsent()
  updateGoogleConsent(preferences)

  if (!shouldLoadMarketingTags(preferences, config)) {
    return false
  }

  return initializeMarketing(config)
}

export const trackMarketingEvent = (
  eventName,
  props = {},
  config = analyticsConfig
) => {
  if (
    !isBrowser() ||
    !shouldLoadMarketingTags(getConsentPreferences(), config)
  ) {
    return false
  }

  initializeMarketing(config)

  const safeProps = sanitizeEventProps(props)

  if (config.marketing.gtmId) {
    window.dataLayer.push({
      event: eventName,
      ...safeProps,
    })
    return true
  }

  if (config.marketing.ga4MeasurementId && typeof window.gtag === "function") {
    window.gtag("event", eventName, safeProps)
    return true
  }

  return false
}

export const trackMarketingPageView = (location, config = analyticsConfig) => {
  const pagePath = location
    ? `${location.pathname || ""}${location.search || ""}${location.hash || ""}`
    : undefined

  return trackMarketingEvent(
    EVENTS.PAGE_VIEW,
    {
      page_path: pagePath,
    },
    config
  )
}
